import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { CheckIcon, CloseIcon, LocationDotIcon, RotateRightIcon } from "../../shared/components/icons";
import { useCameraStream } from "./useCameraStream";
import { captureFileName, fileNames } from "./captureFileName";
import { log } from "../../logger";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import type { FileLocation } from "@egalregal/shared";

export interface CameraShot {
  file: File;
  location: FileLocation | null;
}

interface Shot {
  file: File;
  url: string;
  location: FileLocation | null;
}

/**
 * A phone held sideways: landscape and too short to stack the stage over the
 * footer. A desktop window is landscape too and a tablet in landscape is
 * ≥ 744 px tall, so both keep the centered modal (camera 02).
 */
export const LANDSCAPE_PHONE_QUERY = "(orientation: landscape) and (max-height: 500px)";

/** The landscape controls rail: wide enough for the 72 px shutter plus margins. */
const CONTROLS_RAIL_WIDTH = 112;

/** Short human-readable form of a capture location, e.g. "52.52° N 13.41° E". */
export function formatLocation(location: FileLocation): string {
  const lat = `${Math.abs(location.latitude).toFixed(2)}° ${location.latitude >= 0 ? "N" : "S"}`;
  const lon = `${Math.abs(location.longitude).toFixed(2)}° ${location.longitude >= 0 ? "E" : "W"}`;
  return `${lat} ${lon}`;
}

/**
 * Native-feeling camera UI (ticket 04): full-screen on mobile, modal on
 * desktop. Portrait stacks a video stage over a preview row (last photo as a
 * square, capture time, shortened GEO location) and an action row (close,
 * round shutter, retake). A landscape phone flips the same tree to a row
 * (camera 02): the stage on the left, the preview block, save error and the
 * action stack in a fixed right-hand rail — rotation never remounts the video
 * or clears the stack. The shutter stacks photos; retake removes the last one;
 * close commits the whole stack to the container. Geo is prefetched once per
 * opening into the session store (camera 01) — the shutter never waits for it.
 */
export function CameraDialog({
  open,
  onClose,
  onCapture,
  usedNames = [],
}: {
  open: boolean;
  onClose: () => void;
  onCapture: (shots: CameraShot[]) => Promise<void> | void;
  usedNames?: readonly string[];
}) {
  const t = useT();
  const theme = useTheme();
  // The trigger is "landscape and too short to stack", not device detection.
  const smallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const landscapePhone = useMediaQuery(LANDSCAPE_PHONE_QUERY);
  const fullScreen = smallScreen || landscapePhone;
  const layout = landscapePhone ? "landscape" : "portrait";
  const language = useAppStore((s) => s.language);
  const sessionLocation = useAppStore((s) => s.sessionLocation);
  const sessionLocationPending = useAppStore((s) => s.sessionLocationPending);
  const requestSessionLocation = useAppStore((s) => s.requestSessionLocation);
  const { stream, error } = useCameraStream(open);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [shots, setShots] = useState<Shot[]>([]);
  const [openingLocation, setOpeningLocation] = useState<FileLocation | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setShots([]);
      setBusy(false);
      setSaveError(null);
      setOpeningLocation(useAppStore.getState().sessionLocation);
      requestSessionLocation();
    }
  }, [open, requestSessionLocation]);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      void videoRef.current.play().catch(() => undefined);
    }
  }, [stream]);

  const takePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !stream) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg"));
    if (!blob) return;
    const name = captureFileName("photo", new Date(), [...usedNames, ...fileNames(shots)]);
    const file = new File([blob], name, { type: "image/jpeg" });
    // One fix per opening: a stale session fix pins the whole opening, a
    // missing one tracks the fix as soon as it lands (camera 01).
    const location = openingLocation ?? useAppStore.getState().sessionLocation;
    setShots((prev) => [...prev, { file, url: URL.createObjectURL(file), location }]);
    setSaveError(null);
  };

  const retake = () => {
    setShots((prev) => {
      if (prev.length === 0) return prev;
      const rest = prev.slice(0, -1);
      URL.revokeObjectURL(prev[prev.length - 1]!.url);
      return rest;
    });
  };

  const commit = async () => {
    if (shots.length === 0 || busy) {
      onClose();
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      // Backfill coordless shots with the fix that landed since capture (camera 01).
      const sessionFix = useAppStore.getState().sessionLocation;
      await onCapture(shots.map((s) => ({ file: s.file, location: s.location ?? sessionFix })));
      for (const shot of shots) URL.revokeObjectURL(shot.url);
      onClose();
    } catch (err) {
      log.warn({ err }, "Failed to save captured photos");
      setSaveError(t("cameraSaveError"));
      setBusy(false);
    }
  };

  const last = shots[shots.length - 1];
  const timeLabel = last ? new Date(last.location?.capturedAt ?? Date.now()).toLocaleTimeString(language) : "";
  const gpsPending = sessionLocationPending && sessionLocation === null;

  return (
    <Dialog
      open={open}
      onClose={() => void commit()}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      data-testid="camera-dialog"
    >
      <DialogContent
        data-layout={layout}
        sx={{
          p: 0,
          display: "flex",
          flexDirection: landscapePhone ? "row" : "column",
          height: "100%",
          bgcolor: "black",
        }}
      >
        {error ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            {t("captureCameraError")}
          </Typography>
        ) : (
          <Box
            sx={{
              position: "relative",
              flexGrow: 1,
              minWidth: 0,
              minHeight: landscapePhone ? 0 : 240,
              height: landscapePhone ? "100%" : undefined,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#111",
            }}
          >
            <video
              ref={videoRef}
              muted
              playsInline
              autoPlay
              data-testid="camera-preview"
              style={{ width: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
            />
            <canvas ref={canvasRef} hidden />

            <Box
              data-testid="camera-gps"
              data-pending={gpsPending}
              sx={{
                position: "absolute",
                left: 8,
                bottom: 8,
                zIndex: 1,
                lineHeight: 0,
                color: "#fff",
                opacity: 0.5,
                "@keyframes cameraGpsBlink": {
                  "0%, 100%": { opacity: 0.5 },
                  "50%": { opacity: 0.15 },
                },
                animation: gpsPending ? "cameraGpsBlink 1.2s ease-in-out infinite" : "none",
              }}
            >
              <LocationDotIcon fontSize="small" />
            </Box>
          </Box>
        )}

        <Box
          data-testid="camera-controls"
          sx={{
            position: "relative",
            bgcolor: "background.paper",
            ...(landscapePhone
              ? {
                  // Content-box so the fixed rail keeps its full width for the
                  // shutter and the safe-area padding grows the rail outwards.
                  boxSizing: "content-box",
                  width: CONTROLS_RAIL_WIDTH,
                  flexShrink: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  // A notch/rounded corner on the right must not clip the shutter.
                  pr: "env(safe-area-inset-right)",
                }
              : {}),
          }}
        >
          {/* The preview belongs to the stage: no stage, no preview (as before). */}
          {!error && last && (
            <Stack
              direction={landscapePhone ? "column" : "row"}
              spacing={landscapePhone ? 0.5 : 1.5}
              alignItems={landscapePhone ? "center" : "flex-end"}
              data-testid="camera-preview-row"
              sx={
                landscapePhone
                  ? { width: "100%", minWidth: 0, px: 0.75, py: 1 }
                  : {
                      position: "absolute",
                      // Above the footer = the bottom of the stage, as before.
                      bottom: "100%",
                      left: 0,
                      right: 0,
                      pl: 4,
                      pr: 2,
                      py: 1,
                      background: "rgba(0, 0, 0, 0.35)",
                    }
              }
            >
              <Box
                component="img"
                src={last.url}
                alt={t("capturePreview")}
                data-testid="camera-preview-thumb"
                sx={{
                  maxHeight: landscapePhone ? 56 : 80,
                  maxWidth: landscapePhone ? "100%" : 112,
                  objectFit: "contain",
                  borderRadius: 1,
                }}
              />
              <Box sx={landscapePhone ? { minWidth: 0, textAlign: "center" } : undefined}>
                <Typography variant="caption" color="rgba(255, 255, 255, 0.92)" display="block">
                  {timeLabel}
                </Typography>
                {last.location && (
                  <Typography
                    variant="caption"
                    color="rgba(255, 255, 255, 0.92)"
                    display="block"
                    data-testid="camera-location"
                    sx={landscapePhone ? { wordBreak: "break-word" } : undefined}
                  >
                    {formatLocation(last.location)}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}

          {/* Landscape only: push the error and the actions to the rail's bottom. */}
          <Box sx={{ flexGrow: 1, display: landscapePhone ? "block" : "none" }} />

          {saveError && (
            <Typography variant="body2" color="error" sx={{ px: landscapePhone ? 1 : 2 }}>
              {saveError}
            </Typography>
          )}

          <Stack
            direction={landscapePhone ? "column" : "row"}
            alignItems="center"
            justifyContent={landscapePhone ? "center" : "space-between"}
            spacing={landscapePhone ? 1 : 0}
            sx={
              landscapePhone
                ? { px: 1, pt: 1, pb: "calc(8px + env(safe-area-inset-bottom))" }
                : { px: 2, py: 1.5 }
            }
          >
            <IconButton
              aria-label={shots.length > 0 ? t("captureKeep") : t("close")}
              onClick={() => void commit()}
              disabled={busy}
              data-testid="camera-close"
            >
              {shots.length > 0 ? <CheckIcon /> : <CloseIcon />}
            </IconButton>
            <IconButton
              aria-label={t("captureTakePhoto")}
              onClick={() => void takePhoto()}
              disabled={!stream || busy}
              data-testid="camera-shutter"
              sx={{
                width: 72,
                height: 72,
                border: "4px solid",
                borderColor: "text.secondary",
                borderRadius: "50%",
                bgcolor: "error.main",
                "&:hover": { bgcolor: "error.dark" },
              }}
            />
            <IconButton
              aria-label={t("captureRetake")}
              onClick={retake}
              disabled={shots.length === 0 || busy}
              data-testid="camera-retake"
            >
              <RotateRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
